import { message } from "antd";
import axios from "axios";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  cancelEHRTransfer,
  getActiveTransfer,
  selectTransferAppointment,
  startEHRTransfer,
  submitTransferMFACode,
} from "../../service/notes.service";
import type {
  EHRCredentialCheckResponse,
  EHRTransferStatus,
  StartEHRTransferResponse,
  TransferJob,
} from "../../types/index.type";
import { eventEmitter } from "../../utils/eventEmitter";
import { type AppointmentRow, mergeAppointmentRows, toAppointmentRows } from "./appointmentRows";
import type { TargetEntry } from "./transferTarget";
import { useTransferTargets } from "./useTransferTargets";

// Polling cadences. Active transfer keeps the existing 5s rhythm; idle backs
// off so the app doesn't hammer the API just sitting on the dashboard.
const ACTIVE_POLL_INTERVAL_MS = 5000;
const IDLE_POLL_INTERVAL_MS = 10000;
const ACTIVE_STATUSES: EHRTransferStatus[] = [
  "pending",
  "logging_in",
  "awaiting_mfa",
  "patient_lookup",
  "awaiting_appointment",
  "awaiting_note_type",
  "template_mapping",
  "writing_note",
];
const TERMINAL_STATUSES: EHRTransferStatus[] = ["completed", "failed", "cancelled"];

// Whether a job is still writing (or about to write) the note to the EHR.
// The note editor's Save disables on this: the transfer carries the
// click-time snapshot, so an edit saved now would never reach the chart.
export const isTransferInFlight = (status?: EHRTransferStatus | null): boolean =>
  !!status && ACTIVE_STATUSES.includes(status);
// Statuses that block on the user. At most one of these is surfaced as the
// "primary" job driving the appointment/MFA modals at a time.
const AWAITING_INPUT_STATUSES: EHRTransferStatus[] = ["awaiting_mfa", "awaiting_appointment", "awaiting_note_type"];
// The two picker statuses selectAppointment resolves (the department picker
// reuses awaiting_appointment).
const AWAITING_SELECTION_STATUSES: EHRTransferStatus[] = ["awaiting_appointment", "awaiting_note_type"];
// How long a confirmed selection outranks a stale server status: the backend
// consumes the selection on a 2s tick, so a poll inside the window still
// reports awaiting_* — trusting it would flip the UI back to "Action needed".
// Past the window the server is right again (e.g. the workflow died before
// consuming, and the picker must honestly come back).
const SELECTION_GRACE_MS = 15_000;

export interface SubmittedSelection {
  jobId: string;
  at: number;
}

// Within the grace window, a server job still reporting a picker status is
// coerced to the status the backend sets on consumption (template_mapping).
// Terminal and every other status pass through untouched. Exported for tests.
export const applySelectionGrace = (
  jobs: TransferJob[],
  submitted: SubmittedSelection | null,
  now: number = Date.now(),
): TransferJob[] => {
  if (!submitted || now - submitted.at > SELECTION_GRACE_MS) return jobs;
  return jobs.map((j) =>
    j.id === submitted.jobId && AWAITING_SELECTION_STATUSES.includes(j.status)
      ? { ...j, status: "template_mapping" as EHRTransferStatus }
      : j,
  );
};
const CHROME_EXTENSION_URL =
  "https://chromewebstore.google.com/detail/khnjmojbacjmfbplefmcinlmlbifhjnj?utm_source=item-share-cb";

// With several transfers in flight, exactly one drives the shared modals. Pick
// the one waiting on the user first (so pickers surface promptly), otherwise
// the most-recent active job, otherwise the most-recent (terminal) job for the
// success/error flash. Server returns jobs newest-first.
const pickPrimaryJob = (jobs: TransferJob[]): TransferJob | null => {
  if (!jobs.length) return null;
  return (
    jobs.find((j) => AWAITING_INPUT_STATUSES.includes(j.status)) ??
    jobs.find((j) => ACTIVE_STATUSES.includes(j.status)) ??
    jobs[0]
  );
};

const getUserFriendlyError = (error?: string): string => {
  if (!error) return "Transfer failed. Please try again or contact support.";
  // The client field is labelled "MRN/Patient ID" (some EHRs use a Patient ID, not
  // an MRN); reflect that in any backend-originated message we surface verbatim
  // (e.g. "Please add MRN and DOB…", "MRN … not found").
  return error.replace(/\bMRN\b/g, "MRN/Patient ID");
};

// What startTransfer resolved to. jobId is set whenever a transfer job exists
// for the note — including the "in_progress" error (an already-running job a
// caller may still want to attach follow-up work to). code carries the error
// code when the call didn't start a fresh transfer.
export interface StartTransferResult {
  jobId: string | null;
  code?: string;
}

interface TransferContextValue {
  isLoading: boolean;
  credentialStatus: EHRCredentialCheckResponse | null;
  // The "primary" job — the one currently driving the shared appointment/MFA
  // modals (see pickPrimaryJob). Kept for the modal/status plumbing.
  activeJob: TransferJob | null;
  // Every in-flight (+ recently-finished) transfer. The banner renders all of
  // these; each note's button reads its own entry via getJobForAudio.
  activeJobs: TransferJob[];
  currentStatus: EHRTransferStatus;
  jobId: string | null;
  // Picker rows for the appointment/note-template modal. Held as append-only
  // rows (not raw options) so background refreshes can only append or flag —
  // see mergeAppointmentRows for the invariants.
  appointmentRows: AppointmentRow[];
  // When a background refresh last CHANGED the open picker's rows (drives the
  // "Updated just now" whisper). null until then; reset on every fresh open.
  appointmentsRefreshedAt: number | null;
  // Force an immediate re-request of the appointment list (empty-state retry).
  refreshAppointments: () => void;
  showAppointmentModal: boolean;
  isSelectingAppointment: boolean;
  isNoteTypeSelection: boolean;
  showMFAModal: boolean;
  isSubmittingMFA: boolean;
  mfaOptions: Array<{ id: string; label: string }>;
  // Start a transfer. `appointmentId` is the one-click button's pick (the
  // suggested row or a caret choice); omitted = today's kickoff exactly.
  startTransfer: (audioId: number, appointmentId?: string | null) => Promise<StartTransferResult>;
  selectAppointment: (appointmentId: string) => Promise<void>;
  cancelAppointment: () => void;
  submitMFACode: (code: string, method?: string) => Promise<void>;
  cancelMFA: () => void;
  // Cancel a specific in-flight transfer (banner cancel button).
  cancelTransfer: (jobId: string) => Promise<void>;
  // The most relevant live job for a given note, or null.
  getJobForAudio: (audioId: number) => TransferJob | null;
  // One-click target per note (see transferTarget.ts). ensureTarget fetches
  // once per note (both button mounts share it); refreshTarget forces a
  // re-read (caret Refresh, after a completed transfer, on tab return).
  getTargetForAudio: (audioId: number) => TargetEntry;
  ensureTarget: (audioId: number) => void;
  // Pair of ensureTarget for a mount going away: only notes on screen are
  // re-read on tab return or after another transfer completes.
  releaseTarget: (audioId: number) => void;
  // `ehrRefresh` re-reads the schedule from the EHR itself (the caret's
  // Refresh / Check again). Plain refreshes only re-read the cache.
  refreshTarget: (audioId: number, ehrRefresh?: boolean) => void;
  // The appointment id the last kickoff for this note asked for, so Retry
  // aims at the same row instead of recomputing.
  getLastRequestedTarget: (audioId: number) => string | null;
}

const TransferContext = createContext<TransferContextValue | null>(null);

export const useTransfer = () => {
  const context = useContext(TransferContext);
  if (!context) {
    throw new Error("useTransfer must be used within TransferProvider");
  }
  return context;
};

export const useOptionalTransfer = () => useContext(TransferContext);

interface TransferProviderProps {
  children: React.ReactNode;
}

export const TransferProvider: React.FC<TransferProviderProps> = ({ children }) => {
  const { userInfo } = useAppSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(false);
  const [activeJob, setActiveJob] = useState<TransferJob | null>(null);
  const [activeJobs, setActiveJobs] = useState<TransferJob[]>([]);
  const [currentStatus, setCurrentStatus] = useState<EHRTransferStatus>("not_started");
  const [jobId, setJobId] = useState<string | null>(null);
  const [appointmentRows, setAppointmentRows] = useState<AppointmentRow[]>([]);
  const [appointmentsRefreshedAt, setAppointmentsRefreshedAt] = useState<number | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [isSelectingAppointment, setIsSelectingAppointment] = useState(false);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [isSubmittingMFA, setIsSubmittingMFA] = useState(false);
  const [mfaOptions, setMfaOptions] = useState<Array<{ id: string; label: string }>>([]);
  const lastRequestedTargetRef = useRef<Record<number, string>>({});

  // Derive credentialStatus from Redux. useMemo so it's a stable ref —
  // otherwise startTransfer's deps churn on every render.
  const credentialStatus = useMemo<EHRCredentialCheckResponse>(
    () => ({ has_credential: userInfo.enable_ehr_transfer ?? false }),
    [userInfo.enable_ehr_transfer],
  );

  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Latest poll() fn, exposed so imperative callers (startTransfer) can force
  // an immediate refresh instead of waiting out the idle poll interval.
  const pollRef = useRef<() => void>(() => {});
  // Modal-open dedupe: prevents reopening the same picker while paused.
  const lastModalKeyRef = useRef<string | null>(null);
  // Mirror of appointmentRows so the (stable) poll callback can merge against
  // the current rows without re-creating on every row change.
  const appointmentRowsRef = useRef<AppointmentRow[]>([]);
  // MFA submission lock: don't re-show the modal after the user has submitted.
  const mfaSubmittedForJobRef = useRef<string | null>(null);
  // Last terminal status we already broadcast as a `ehrTransferCompleted` /
  // `ehrTransferFailed` event, keyed by `${jobId}:${status}`. Prevents
  // duplicate broadcasts when the recent-terminal window keeps the same job
  // visible across multiple polls.
  const lastTerminalKeyRef = useRef<string | null>(null);
  // Mirror of currentStatus + showMFAModal so the polling effect can read
  // them without re-subscribing whenever they change. Without this, every
  // status transition tears down and restarts the polling timer, which can
  // burst poll the API and is brittle around the visibilitychange listener.
  const currentStatusRef = useRef<EHRTransferStatus>("not_started");
  const showMFAModalRef = useRef<boolean>(false);
  // The user's confirmed picker selection (same idiom as the MFA submit lock):
  // consulted by the poll's applySelectionGrace so a stale server status can't
  // flip the banner/button back to "Action needed" after the click.
  const selectionSubmittedRef = useRef<SubmittedSelection | null>(null);
  // Mirrors for handleActiveUpdate's close-on-advance: the picker must close
  // when the SERVER moves the job past selection (backend auto-select — the
  // wedged "Preparing transfer…" modal), but never mid user-select call.
  const showAppointmentModalRef = useRef<boolean>(false);
  const isSelectingAppointmentRef = useRef<boolean>(false);
  // Mirror of the primary jobId so stable callbacks (cancelTransfer) can read
  // it without re-creating on every status change.
  const jobIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentStatusRef.current = currentStatus;
  }, [currentStatus]);
  useEffect(() => {
    showMFAModalRef.current = showMFAModal;
  }, [showMFAModal]);
  useEffect(() => {
    showAppointmentModalRef.current = showAppointmentModal;
  }, [showAppointmentModal]);
  useEffect(() => {
    isSelectingAppointmentRef.current = isSelectingAppointment;
  }, [isSelectingAppointment]);
  useEffect(() => {
    jobIdRef.current = jobId;
  }, [jobId]);

  const handleActiveUpdate = useCallback((job: TransferJob | null) => {
    setActiveJob(job);

    if (!job) {
      // No active or recently-finished job — reset transient picker state.
      setJobId(null);
      setCurrentStatus("not_started");
      setShowAppointmentModal(false);
      setShowMFAModal(false);
      return;
    }

    setJobId(job.id);
    setCurrentStatus(job.status);

    // MFA: open modal on transition into awaiting_mfa; respect submit lock.
    if (job.status === "awaiting_mfa" && job.requires_mfa_code && mfaSubmittedForJobRef.current !== job.id) {
      if (!showMFAModalRef.current) {
        setMfaOptions(job.mfa_options || []);
        setShowMFAModal(true);
      }
    }

    // Appointment / note-type pickers: status itself is the discriminator.
    const isAwaitingSelection =
      (job.status === "awaiting_appointment" && job.requires_appointment_selection) ||
      (job.status === "awaiting_note_type" && job.requires_note_type_selection);
    // Close-on-advance: the server moved past selection without a local click
    // (backend auto-select) — an open picker would wedge on "Preparing
    // transfer…". Never fires mid user-select; clearing the modal-key lets a
    // genuine return to awaiting reopen a fresh picker.
    if (!isAwaitingSelection && showAppointmentModalRef.current && !isSelectingAppointmentRef.current) {
      setShowAppointmentModal(false);
      lastModalKeyRef.current = null;
    }
    if (isAwaitingSelection && job.appointment_options) {
      const modalKey = `${job.id}:${job.status}`;
      if (modalKey !== lastModalKeyRef.current) {
        // Fresh picker: seed the rows and open. An empty list still opens —
        // the modal renders its empty state with a retry affordance.
        appointmentRowsRef.current = toAppointmentRows(job.appointment_options);
        setAppointmentRows(appointmentRowsRef.current);
        setAppointmentsRefreshedAt(null);
        setShowAppointmentModal(true);
        lastModalKeyRef.current = modalKey;
      } else if (job.appointment_options.length > 0) {
        // Background refresh for the already-seeded picker: merge append-only
        // (rows never move or change identity — see mergeAppointmentRows).
        // Skip empty payloads: a transient empty refresh must not flag the
        // whole list "no longer on schedule".
        const merged = mergeAppointmentRows(appointmentRowsRef.current, job.appointment_options);
        if (merged !== appointmentRowsRef.current) {
          appointmentRowsRef.current = merged;
          setAppointmentRows(merged);
          setAppointmentsRefreshedAt(Date.now());
        }
      }
    }

    // Terminal events: emit once per (job, status) pair so listeners can
    // react without duplicate fires across the recent-terminal poll window.
    if (TERMINAL_STATUSES.includes(job.status)) {
      const terminalKey = `${job.id}:${job.status}`;
      if (lastTerminalKeyRef.current !== terminalKey) {
        lastTerminalKeyRef.current = terminalKey;
        if (job.status === "completed") {
          eventEmitter.emit("ehrTransferCompleted", { audioId: job.audio_file_id ?? undefined });
        } else if (job.status === "failed") {
          eventEmitter.emit("ehrTransferFailed", { audioId: job.audio_file_id ?? undefined });
        }
      }
    }
  }, []);

  // Declared here (above the target hook that gates on it) rather than next
  // to the poll effect below, which reads it too.
  const ehrTransferEnabled = Boolean(userInfo?.enable_ehr_transfer);

  const { getTargetForAudio, ensureTarget, releaseTarget, refreshTarget, refreshTargetRef } =
    useTransferTargets(ehrTransferEnabled);
  const getLastRequestedTarget = useCallback(
    (audioId: number): string | null => lastRequestedTargetRef.current[audioId] ?? null,
    [],
  );

  // Poll the global active-transfer endpoint. Mounted once for the lifetime
  // of the provider; cadence reads from currentStatusRef so status changes
  // don't tear down and restart the timer.
  //
  // Skip entirely when the user doesn't have the EHR transfer feature.
  // `/templates` is rendered inside `AuthLayout avoidTokenValidation`, so
  // unauthenticated visitors would otherwise be 401-spammed every 5–30s.
  // `enable_ehr_transfer` is false until userInfo loads after a successful
  // session, so this gate also covers the not-logged-in case.
  useEffect(() => {
    if (!ehrTransferEnabled) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await getActiveTransfer();
        if (cancelled) return;
        // Prefer the multi-job list; fall back to the legacy single `job`.
        const rawJobs: TransferJob[] = res.data?.jobs ?? (res.data?.job ? [res.data.job] : []);
        // A confirmed picker click outranks a stale awaiting_* for the grace
        // window (the backend consumes the selection on its own 2s tick).
        const jobs = applySelectionGrace(rawJobs, selectionSubmittedRef.current);
        setActiveJobs(jobs);
        // One primary job drives the shared modals + status; the rest render
        // in the banner. This keeps the single-transfer flow byte-identical.
        handleActiveUpdate(pickPrimaryJob(jobs));
      } catch (error) {
        // 401 means the session expired or the user logged out mid-poll. No
        // point retrying — the axios interceptor handles the redirect, and
        // looping here just floods Sentry.
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          return;
        }
        console.error("Error polling active transfer:", error);
      }

      if (cancelled) return;

      const isTabHidden = typeof document !== "undefined" && document.visibilityState === "hidden";
      if (isTabHidden) {
        // Don't schedule another poll while hidden; visibilitychange below
        // will kick us back in when the tab becomes active.
        return;
      }

      const next = ACTIVE_STATUSES.includes(currentStatusRef.current) ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS;
      pollingTimeoutRef.current = setTimeout(poll, next);
    };

    // Expose poll so imperative callers (startTransfer) can refresh on demand.
    pollRef.current = poll;

    // Kick off immediately on mount.
    poll();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        poll();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [handleActiveUpdate, ehrTransferEnabled]);

  const startTransfer = useCallback(
    async (audioId: number, appointmentId?: string | null): Promise<StartTransferResult> => {
      if (!audioId) {
        message.error("Invalid note ID");
        return { jobId: null, code: "invalid" };
      }

      if (!credentialStatus?.has_credential) {
        // RPA-enabled groups transfer server-side, not via the copilot extension.
        // Don't bounce them to the Chrome Web Store while their EHR credential is
        // still being provisioned — just let them know it's being set up.
        if (userInfo.group_rpa_enabled) {
          message.info("EHR transfer is being set up for your practice. Please contact support if this persists.");
          return { jobId: null, code: "no_credential_rpa" };
        }
        window.open(CHROME_EXTENSION_URL, "_blank");
        return { jobId: null, code: "no_credential" };
      }

      setIsLoading(true);
      // Retry reuses the appointment THIS attempt aimed at — never an older
      // one-click pick after a later attempt went through the picker.
      if (appointmentId) lastRequestedTargetRef.current[audioId] = appointmentId;
      else delete lastRequestedTargetRef.current[audioId];

      try {
        const res = await startEHRTransfer(audioId, appointmentId ?? undefined);
        const data: StartEHRTransferResponse = res.data;

        if (data.success) {
          if (data.code === "appointment_gone") {
            // The clicked row left the schedule between read and click. The
            // job is parked in the picker with the fresh list; say so and
            // re-read the target so the caret agrees with what the EHR has.
            message.info("That appointment is no longer on the schedule — pick one from the list");
            refreshTargetRef.current(audioId);
          } else {
            message.info("Transfer started");
          }
          const status = data.status || "pending";
          setCurrentStatus(status);
          setJobId(data.job_id || null);
          // Optimistically flip this note's button (and the banner) without
          // waiting for the next poll. Upsert so other in-flight jobs survive.
          if (data.job_id) {
            const optimistic: TransferJob = {
              id: data.job_id,
              status,
              patient_name: "",
              requires_appointment_selection: false,
              audio_file_id: audioId,
            };
            setActiveJob(optimistic);
            setActiveJobs((prev) => {
              const without = prev.filter((j) => j.id !== optimistic.id);
              return [optimistic, ...without];
            });
          }
          eventEmitter.emit("ehrTransferStarted", { audioId });

          // The picker modal is opened by handleActiveUpdate from a poll
          // result, not from the optimistic activeJob above (which carries no
          // appointment_options). On the cached-appointments path the job is
          // already awaiting selection server-side, so force an immediate poll
          // instead of waiting up to IDLE_POLL_INTERVAL_MS for the next tick.
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          pollRef.current();
          return { jobId: data.job_id || null };
        }
        return { jobId: data.job_id || null, code: onStartRefused(audioId, handleTransferError(data)) };
      } catch (error) {
        console.error("Error starting EHR transfer:", error);
        const axiosError = error as { response?: { data?: StartEHRTransferResponse } };
        const errData = axiosError.response?.data;
        return { jobId: errData?.job_id || null, code: onStartRefused(audioId, handleTransferError(errData)) };
      } finally {
        setIsLoading(false);
      }
    },
    [credentialStatus, userInfo.group_rpa_enabled, refreshTargetRef],
  );

  // A one-click refused because its schedule is gone: re-read the target so
  // the button shows what the cache holds now.
  const onStartRefused = (audioId: number, code: string | undefined): string | undefined => {
    if (code === "schedule_changed") refreshTargetRef.current(audioId);
    return code;
  };

  const handleTransferError = (data?: StartEHRTransferResponse): string | undefined => {
    const errorCode = data?.code;

    switch (errorCode) {
      case "no_credential":
        // RPA groups never get sent to the Chrome Web Store (see startTransfer).
        if (userInfo.group_rpa_enabled) {
          message.info("EHR transfer is being set up for your practice. Please contact support if this persists.");
          break;
        }
        window.open(CHROME_EXTENSION_URL, "_blank");
        break;
      case "schedule_changed":
        // The one-click's schedule is gone (expired / re-read). Nothing was
        // started; the caller re-reads the target.
        message.info("The schedule changed — check the appointment and try again");
        break;
      case "already_transferred":
        message.info("This note was already transferred");
        break;
      case "in_progress":
        message.info("Transfer is already in progress");
        setCurrentStatus(data?.status || "pending");
        setJobId(data?.job_id || null);
        break;
      case "no_client":
        message.error("Note has no associated client/patient");
        break;
      case "missing_patient_name":
      case "missing_patient_dob":
        message.error("Patient information is incomplete in client record");
        break;
      default:
        message.error(getUserFriendlyError(data?.error));
    }
    return errorCode;
  };

  const selectAppointment = useCallback(
    async (appointmentId: string) => {
      if (!jobId) {
        message.error("No active transfer job");
        return;
      }

      setIsSelectingAppointment(true);
      const wasNoteTypeSelection = currentStatusRef.current === "awaiting_note_type";

      try {
        const response = await selectTransferAppointment(jobId, appointmentId);
        setShowAppointmentModal(false);

        const responseStatus = response?.data?.status;
        let optimisticStatus: EHRTransferStatus;
        if (responseStatus === "completed") {
          optimisticStatus = "completed";
          message.success("Note transferred to EHR successfully!");
        } else if (responseStatus === "failed") {
          optimisticStatus = "failed";
          message.error(getUserFriendlyError(response?.data?.error_message));
        } else {
          optimisticStatus = "template_mapping";
          message.info(
            wasNoteTypeSelection
              ? "Note template selected. Transfer in progress."
              : "Appointment selected. Transfer in progress.",
          );
        }
        setCurrentStatus(optimisticStatus);
        // The banner and each note's button render from activeJobs, so upsert
        // the confirmed selection there too — otherwise "Action needed"
        // survives the click until a poll lands after the consume tick.
        setActiveJob((prev) => (prev && prev.id === jobId ? { ...prev, status: optimisticStatus } : prev));
        setActiveJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, status: optimisticStatus } : j)));
        // Guard the next polls from reverting it, then reconcile promptly
        // instead of waiting out the tick (same move as cancelTransfer).
        selectionSubmittedRef.current = { jobId, at: Date.now() };
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        pollRef.current();
      } catch (error) {
        console.error("Error selecting appointment:", error);
        message.error(wasNoteTypeSelection ? "Failed to select note template" : "Failed to select appointment");
      } finally {
        setIsSelectingAppointment(false);
      }
    },
    [jobId],
  );

  const cancelAppointment = useCallback(async () => {
    setShowAppointmentModal(false);
    if (jobId) {
      setCurrentStatus("cancelled");
      try {
        await cancelEHRTransfer(jobId);
      } catch (error) {
        console.error("Failed to cancel transfer job:", error);
      }
    }
  }, [jobId]);

  const submitMFACode = useCallback(
    async (code: string, method?: string) => {
      if (!jobId) {
        message.error("No active transfer job");
        return;
      }

      setIsSubmittingMFA(true);

      try {
        await submitTransferMFACode(jobId, code || undefined, method);

        if (method && !code) {
          setShowMFAModal(false);
          setMfaOptions([]);
          message.info("Verification method selected. Waiting for code prompt...");
        } else {
          mfaSubmittedForJobRef.current = jobId;
          setShowMFAModal(false);
          setCurrentStatus("logging_in");
          message.info("Verification code submitted. Transfer resuming...");
        }
      } catch (error) {
        console.error("Error submitting MFA:", error);
        message.error("Failed to submit verification. Please try again.");
      } finally {
        setIsSubmittingMFA(false);
      }
    },
    [jobId],
  );

  const cancelMFA = useCallback(() => {
    setShowMFAModal(false);
  }, []);

  // Empty-state retry: force an immediate poll — there is no dedicated
  // appointments-refresh endpoint; the active poll IS the refresh machinery.
  const refreshAppointments = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    pollRef.current();
  }, []);

  // Cancel a specific in-flight transfer (banner cancel button). Optimistically
  // marks it cancelled, closes the shared modals if they were this job's, then
  // re-syncs from the server.
  const cancelTransfer = useCallback(async (id: string) => {
    setActiveJobs((prev) => prev.map((j) => (j.id === id ? { ...j, status: "cancelled" } : j)));
    if (jobIdRef.current === id) {
      setShowAppointmentModal(false);
      setShowMFAModal(false);
      setCurrentStatus("cancelled");
    }
    try {
      await cancelEHRTransfer(id);
      message.info("Transfer cancelled");
    } catch (error) {
      console.error("Failed to cancel transfer job:", error);
      message.error("Failed to cancel transfer. Please try again.");
    } finally {
      pollRef.current();
    }
  }, []);

  // The most relevant live job for a note: prefer a non-terminal one, else the
  // most recent entry for that audio.
  const getJobForAudio = useCallback(
    (audioId: number): TransferJob | null => {
      const forAudio = activeJobs.filter((j) => j.audio_file_id === audioId);
      if (!forAudio.length) return null;
      return forAudio.find((j) => !TERMINAL_STATUSES.includes(j.status)) ?? forAudio[0];
    },
    [activeJobs],
  );

  const isNoteTypeSelection = currentStatus === "awaiting_note_type";

  const value: TransferContextValue = {
    isLoading,
    credentialStatus,
    activeJob,
    activeJobs,
    currentStatus,
    jobId,
    appointmentRows,
    appointmentsRefreshedAt,
    refreshAppointments,
    showAppointmentModal,
    isSelectingAppointment,
    isNoteTypeSelection,
    showMFAModal,
    isSubmittingMFA,
    mfaOptions,
    startTransfer,
    selectAppointment,
    cancelAppointment,
    submitMFACode,
    cancelMFA,
    cancelTransfer,
    getJobForAudio,
    getTargetForAudio,
    ensureTarget,
    releaseTarget,
    refreshTarget,
    getLastRequestedTarget,
  };

  return <TransferContext.Provider value={value}>{children}</TransferContext.Provider>;
};
